import { useState, useEffect, useCallback } from "react";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";
import { getStartingMonth } from "../../../../utils/PeriodCalculationUtils";

// ─── Shared constants & formatters ───────────────────────────────────────────
export const PERIOD_TYPE_LABELS = {
  YEARLY: "Yearly",
  HALF_YEARLY: "Half Yearly",
  QUARTERLY: "Quarterly",
  MONTHLY: "Monthly",
};

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export const formatDateRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) return "—";
  const from = new Date(fromDate);
  const to = new Date(toDate);
  const sameMonth =
    from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth();
  if (sameMonth) return `${MONTH_ABBR[from.getMonth()]} ${from.getFullYear()}`;
  return `${MONTH_ABBR[from.getMonth()]} ${from.getFullYear()} – ${MONTH_ABBR[to.getMonth()]} ${to.getFullYear()}`;
};

// ─── Period slot generator ───────────────────────────────────────────────────
// Returns [{ label, fromDate, toDate }] for the given period type,
// derived purely from the financial year label + company starting month.
export const buildPeriodSlots = (fyLabel, startingMonth) => {
  if (!fyLabel || !startingMonth) return {};

  const sm = startingMonth;
  const startYear = parseInt(fyLabel.split("-")[0], 10);
  const pad = (n) => String(n).padStart(2, "0");
  const getLastDay = (y, m) => new Date(y, m, 0).getDate();
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const monthly = Array.from({ length: 12 }, (_, i) => {
    const offset = sm - 1 + i;
    const calMonth = (offset % 12) + 1;
    const calYear = startYear + Math.floor(offset / 12);
    const lastDay = getLastDay(calYear, calMonth);
    return {
      label: `${M[calMonth - 1]} ${calYear}`,
      fromDate: `${calYear}-${pad(calMonth)}-01`,
      toDate: `${calYear}-${pad(calMonth)}-${pad(lastDay)}`,
    };
  });

  const quarterly = Array.from({ length: 4 }, (_, i) => {
    const so = sm - 1 + i * 3;
    const sMon = (so % 12) + 1;
    const sYr = startYear + Math.floor(so / 12);
    const eo = sm - 1 + i * 3 + 2;
    const eMon = (eo % 12) + 1;
    const eYr = startYear + Math.floor(eo / 12);
    return {
      label: `Q${i + 1} (${M[sMon - 1]} ${sYr} - ${M[eMon - 1]} ${eYr})`,
      fromDate: `${sYr}-${pad(sMon)}-01`,
      toDate: `${eYr}-${pad(eMon)}-${pad(getLastDay(eYr, eMon))}`,
    };
  });

  const halfYearly = Array.from({ length: 2 }, (_, i) => {
    const so = sm - 1 + i * 6;
    const sMon = (so % 12) + 1;
    const sYr = startYear + Math.floor(so / 12);
    const eo = sm - 1 + i * 6 + 5;
    const eMon = (eo % 12) + 1;
    const eYr = startYear + Math.floor(eo / 12);
    return {
      label: `H${i + 1} (${M[sMon - 1]} ${sYr} - ${M[eMon - 1]} ${eYr})`,
      fromDate: `${sYr}-${pad(sMon)}-01`,
      toDate: `${eYr}-${pad(eMon)}-${pad(getLastDay(eYr, eMon))}`,
    };
  });

  const eo = sm - 1 + 11;
  const eMon = (eo % 12) + 1;
  const eYr = startYear + Math.floor(eo / 12);
  const yearly = [{
    label: fyLabel,
    fromDate: `${startYear}-${pad(sm)}-01`,
    toDate: `${eYr}-${pad(eMon)}-${pad(getLastDay(eYr, eMon))}`,
  }];

  return { YEARLY: yearly, HALF_YEARLY: halfYearly, QUARTERLY: quarterly, MONTHLY: monthly };
};

// ─── Hook ────────────────────────────────────────────────────────────────────
const useTriggers = () => {

  // ── Base fetched data ──────────────────────────────────────────────────────
  const [financialYears, setFinancialYears] = useState([]);            // { value, label }[]
  const [selectedFinancialYear, setSelectedFinancialYear] = useState("");
  const [startingMonth, setStartingMonth] = useState(1);
  const [sources, setSources] = useState([]);    // { id, unitCode, ... }[]
  const [tags, setTags] = useState([]);          // { id, keyword_type, keyword_value }[]
  const [units, setUnits] = useState([]);        // { id, unit, unit_name }[]
  const [triggers, setTriggers] = useState([]);  // existing saved triggers
  const [questions, setQuestions] = useState([]); // quantitative KPIs
  const [breachedTriggers, setBreachedTriggers] = useState([]);
  const [loadingBreachedTriggers, setLoadingBreachedTriggers] = useState(false);

  // ── Overview tab state ─────────────────────────────────────────────────────
  const [ovw_periodType, setOvw_periodType] = useState("");
  const [ovw_selectedPeriod, setOvw_selectedPeriod] = useState(null);   // { fromDate, toDate, label }
  const [ovw_sourceId, setOvw_sourceId] = useState(null);               // number
  const [ovw_selectedTagValues, setOvw_selectedTagValues] = useState([]); // string[]

  // ── Configure tab state ────────────────────────────────────────────────────
  const [cfg_periodType, setCfg_periodType] = useState("");
  const [cfg_selectedPeriods, setCfg_selectedPeriods] = useState([]);   // { fromDate, toDate, label }[]
  const [cfg_sourceIds, setCfg_sourceIds] = useState([]);               // number[]
  const [cfg_questionIds, setCfg_questionIds] = useState([]);           // number[]
  const [cfg_kpiTagFilter, setCfg_kpiTagFilter] = useState([]);         // tag value string[]
  const [cfg_commonMin, setCfg_commonMin] = useState("");
  const [cfg_commonMax, setCfg_commonMax] = useState("");

  // ── Email Configure tab state ──────────────────────────────────────────────
  const [ecfg_periodType, setEcfg_periodType] = useState("");
  const [ecfg_selectedPeriods, setEcfg_selectedPeriods] = useState([]);
  const [ecfg_sourceIds, setEcfg_sourceIds] = useState([]);
  const [ecfg_questionIds, setEcfg_questionIds] = useState([]);
  const [ecfg_kpiTagFilter, setEcfg_kpiTagFilter] = useState([]);

  // ── Email Overview tab state ───────────────────────────────────────────────
  const [eovw_periodType, setEovw_periodType] = useState("");
  const [eovw_selectedPeriod, setEovw_selectedPeriod] = useState(null);
  const [eovw_sourceId, setEovw_sourceId] = useState(null);
  const [eovw_selectedTagValues, setEovw_selectedTagValues] = useState([]);

  // ── Initial data load ──────────────────────────────────────────────────────
  useEffect(() => {
    setStartingMonth(getStartingMonth());
    getFinancialYears();
    getSource();
    getTag();
    getUnits();
  }, []);

  // ── Load questions + triggers whenever FY changes ──────────────────────────
  useEffect(() => {
    if (selectedFinancialYear) {
      getQuestions();
      getTriggers();
      getBreachedTriggers();
    }
  }, [selectedFinancialYear]);

  // ── Fetch functions ────────────────────────────────────────────────────────

  const getFinancialYears = async () => {
    try {
      const storedData = localStorage.getItem("financialYearsData");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData.length > 0) {
          setFinancialYears(parsedData.map(fy => ({ label: fy.financial_year_value, value: fy.id })));
          setSelectedFinancialYear(parsedData[parsedData.length - 1]?.id);
        }
      } else {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`,
          {},
          {}
        );
        if (isSuccess && data?.data.length > 0) {
          localStorage.setItem("financialYearsData", JSON.stringify(data.data));
          setFinancialYears(data.data.map(fy => ({ label: fy.financial_year_value, value: fy.id })));
          setSelectedFinancialYear(data.data[data.data.length - 1]?.id);
        }
      }
    } catch (error) {
      console.error("Error fetching financial years:", error);
    }
  };

  const getSource = async () => {
    try {
      const response = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSource`,
        {},
        {},
        "GET"
      );
      if (response.isSuccess) {
        setSources(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching sources:", error);
    }
  };

  const getTag = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}keywords`,
      {},
      {},
      "GET"
    );
    if (isSuccess) {
      setTags(data.data);
    }
  };

  const getUnits = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getUnitMaster`,
      {},
      {},
      "GET"
    );
    if (isSuccess) {
      setUnits(data);
    }
  };

  const getBreachedTriggers = useCallback(async () => {
    setLoadingBreachedTriggers(true);
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}triggers/breaches`,
      {},
      { financialYearId: selectedFinancialYear },
      "GET",
      false,
      false,
    );
    setBreachedTriggers(isSuccess && Array.isArray(data?.data) ? data.data : []);
    setLoadingBreachedTriggers(false);
  }, [selectedFinancialYear]);

  const getTriggers = useCallback(async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}triggers/${selectedFinancialYear}`,
      {},
      {},
      "GET"
    );
    if (isSuccess) {
      setTriggers(data.data);
    }
  }, [selectedFinancialYear]);

  const getQuestions = async () => {
    try {
      const [qRes, emissionRes] = await Promise.all([
        apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestion`,
          {},
          { financialYearId: selectedFinancialYear },
          "GET"
        ),
        apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`,
          {},
          { fyid: selectedFinancialYear },
          "GET"
        ),
      ]);
      if (!qRes.isSuccess) return;

      const allQuestions = qRes.data.data.filter(q => q.questionType === "quantitative");
      const allowedKpiIds = Array.isArray(emissionRes?.data?.kpiIds)
        ? new Set(emissionRes.data.kpiIds.map(Number))
        : null;

      setQuestions(
        allowedKpiIds
          ? allQuestions.filter(q => allowedKpiIds.has(Number(q.questionId)))
          : allQuestions
      );
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  // ── Bulk create ────────────────────────────────────────────────────────────

  const bulkCreateTriggers = async (payloads, onProgress) => {
    const CHUNK_SIZE = 200;
    const totalChunks = Math.ceil(payloads.length / CHUNK_SIZE);
    for (let i = 0; i < payloads.length; i += CHUNK_SIZE) {
      const done = Math.floor(i / CHUNK_SIZE) + 1;
      const isLast = done === totalChunks;
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}triggers/bulk`,
        {},
        payloads.slice(i, i + CHUNK_SIZE),
        "POST",
        true,
        isLast
      );
      if (!isSuccess) return false;
      onProgress?.(Math.round((done / totalChunks) * 100));
    }
    return true;
  };

 /*  [
    {
        "financialYearId": 31,
        "questionId": 8849,
        "questionType": "quantitative",
        "sourceId": 5,
        "fromDate": "2025-07-01",
        "toDate": "2026-06-30",
        "emailRecipients": {
            "cc": ["himanshu.saini@riu.ai"],
            "bcc": ["himanshusainib.159@gmail.com"],
            "to": ["abhimanyu@riu.ai"]
        }
    }
] */
  const getBreachSchedule = useCallback(async () => {
    if (!selectedFinancialYear) return {};
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}triggerBreachSchedule`,
      {},
      { financialYearId: selectedFinancialYear },
      "GET",
      false,
      false,
    );
    return isSuccess && data?.data?.schedules ? data.data.schedules : {};
  }, [selectedFinancialYear]);

  const saveBreachSchedule = useCallback(async (schedules) => {
    const { isSuccess } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}triggerBreachSchedule`,
      {},
      { financialYearId: Number(selectedFinancialYear), schedules },
      "POST",
      true,
      true,
    );
    return isSuccess;
  }, [selectedFinancialYear]);

  const bulkUpdateEmailRecipients = async (payloads, onProgress) => {
    const CHUNK_SIZE = 200;
    const totalChunks = Math.ceil(payloads.length / CHUNK_SIZE);
    for (let i = 0; i < payloads.length; i += CHUNK_SIZE) {
      const done = Math.floor(i / CHUNK_SIZE) + 1;
      const isLast = done === totalChunks;
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}triggers/bulk-email-recipients`,
        {},
        payloads.slice(i, i + CHUNK_SIZE),
        "POST",
        true,
        isLast
      );
      if (!isSuccess) return false;
      onProgress?.(Math.round((done / totalChunks) * 100));
    }
    return true;
  };

  // ── Period slot helper ─────────────────────────────────────────────────────
  // Returns all period slots for all types keyed by type string,
  // or just for a specific type if provided.
  const computePeriodSlots = useCallback((periodType) => {
    const fyEntry = financialYears.find(fy => String(fy.value) === String(selectedFinancialYear));
    if (!fyEntry) return [];
    const allSlots = buildPeriodSlots(fyEntry.label, startingMonth);
    return periodType ? (allSlots[periodType] ?? []) : allSlots;
  }, [financialYears, selectedFinancialYear, startingMonth]);

  // ── Unit resolver ─────────────────────────────────────────────────────────
  // Given a question, finds the base unit string using supportedUnits.
  const resolveBaseUnit = useCallback((question) => {
    if (!question?.supportedUnits?.length) return "";
    const baseEntry = question.supportedUnits.find(u => u.is_base === true);
    if (!baseEntry) return "";
    const unitRecord = units.find(u => u.id === baseEntry.unit_id);
    return unitRecord?.unit ?? "";
  }, [units]);

  // Given a question, finds the base unit id from supportedUnits.
  const resolveBaseUnitId = useCallback((question) => {
    if (!question?.supportedUnits?.length) return null;
    const baseEntry = question.supportedUnits.find(u => u.is_base === true);
    return baseEntry?.unit_id ?? null;
  }, []);

  // ── Overview helpers ───────────────────────────────────────────────────────
  // Returns period types that have at least one trigger in the selected FY.
  const getAvailablePeriodTypes = useCallback(() => {
    const fyTriggers = triggers.filter(
      t => String(t.financialYearId) === String(selectedFinancialYear)
    );
    const types = ["YEARLY", "HALF_YEARLY", "QUARTERLY", "MONTHLY"];
    return types.filter(type => {
      const slots = computePeriodSlots(type);
      return slots.some(slot =>
        fyTriggers.some(t => t.fromDate === slot.fromDate && t.toDate === slot.toDate)
      );
    });
  }, [triggers, selectedFinancialYear, computePeriodSlots]);

  // Returns period slots (for selected type) that have at least one trigger.
  const getAvailablePeriodSlots = useCallback((periodType) => {
    if (!periodType) return [];
    const fyTriggers = triggers.filter(
      t => String(t.financialYearId) === String(selectedFinancialYear)
    );
    const slots = computePeriodSlots(periodType);
    return slots.filter(slot =>
      fyTriggers.some(t => t.fromDate === slot.fromDate && t.toDate === slot.toDate)
    );
  }, [triggers, selectedFinancialYear, computePeriodSlots]);

  // Returns sources that have at least one trigger in the selected period.
  const getAvailableSources = useCallback((period) => {
    if (!period) return [];
    const matched = triggers.filter(
      t =>
        String(t.financialYearId) === String(selectedFinancialYear) &&
        t.fromDate === period.fromDate &&
        t.toDate === period.toDate
    );
    const sourceIds = [...new Set(matched.map(t => t.sourceId))];
    return sources.filter(s => sourceIds.includes(s.id));
  }, [triggers, selectedFinancialYear, sources]);

  // ── Email Overview helpers ─────────────────────────────────────────────────
  const getEovw_availablePeriodTypes = useCallback(() => {
    const fyTriggers = triggers.filter(
      t => String(t.financialYearId) === String(selectedFinancialYear) &&
           t.emailRecipients != null
    );
    const types = ["YEARLY", "HALF_YEARLY", "QUARTERLY", "MONTHLY"];
    return types.filter(type => {
      const slots = computePeriodSlots(type);
      return slots.some(slot =>
        fyTriggers.some(t => t.fromDate === slot.fromDate && t.toDate === slot.toDate)
      );
    });
  }, [triggers, selectedFinancialYear, computePeriodSlots]);

  const getEovw_availablePeriodSlots = useCallback((periodType) => {
    if (!periodType) return [];
    const fyTriggers = triggers.filter(
      t => String(t.financialYearId) === String(selectedFinancialYear) &&
           t.emailRecipients != null
    );
    const slots = computePeriodSlots(periodType);
    return slots.filter(slot =>
      fyTriggers.some(t => t.fromDate === slot.fromDate && t.toDate === slot.toDate)
    );
  }, [triggers, selectedFinancialYear, computePeriodSlots]);

  const getEovw_availableSources = useCallback((period) => {
    if (!period) return [];
    const matched = triggers.filter(
      t =>
        String(t.financialYearId) === String(selectedFinancialYear) &&
        t.fromDate === period.fromDate &&
        t.toDate === period.toDate &&
        t.emailRecipients != null
    );
    const sourceIds = [...new Set(matched.map(t => t.sourceId))];
    return sources.filter(s => sourceIds.includes(s.id));
  }, [triggers, selectedFinancialYear, sources]);

  // ── Email Configure helpers ────────────────────────────────────────────────
  // Period types that have ≥ 1 trigger in the selected FY.
  const getEcfg_availablePeriodTypes = useCallback(() => {
    const fyTriggers = triggers.filter(
      t => String(t.financialYearId) === String(selectedFinancialYear)
    );
    const types = ["YEARLY", "HALF_YEARLY", "QUARTERLY", "MONTHLY"];
    return types.filter(type => {
      const slots = computePeriodSlots(type);
      return slots.some(slot =>
        fyTriggers.some(t => t.fromDate === slot.fromDate && t.toDate === slot.toDate)
      );
    });
  }, [triggers, selectedFinancialYear, computePeriodSlots]);

  // Period slots of a given type that have ≥ 1 trigger in the selected FY.
  const getEcfg_availablePeriodSlots = useCallback((periodType) => {
    if (!periodType) return [];
    const fyTriggers = triggers.filter(
      t => String(t.financialYearId) === String(selectedFinancialYear)
    );
    const slots = computePeriodSlots(periodType);
    return slots.filter(slot =>
      fyTriggers.some(t => t.fromDate === slot.fromDate && t.toDate === slot.toDate)
    );
  }, [triggers, selectedFinancialYear, computePeriodSlots]);

  // Sources that have ≥ 1 trigger for ANY of the given selected periods.
  const getEcfg_availableSources = useCallback((selectedPeriods) => {
    if (!selectedPeriods || selectedPeriods.length === 0) return [];
    const matched = triggers.filter(
      t =>
        String(t.financialYearId) === String(selectedFinancialYear) &&
        selectedPeriods.some(p => p.fromDate === t.fromDate && p.toDate === t.toDate)
    );
    const sourceIds = [...new Set(matched.map(t => t.sourceId))];
    return sources.filter(s => sourceIds.includes(s.id));
  }, [triggers, selectedFinancialYear, sources]);

  // Questions/KPIs that have ≥ 1 trigger for ANY (period, source) combo from the selection.
  const getEcfg_availableQuestions = useCallback((selectedPeriods, selectedSourceIds) => {
    if (!selectedPeriods || selectedPeriods.length === 0) return [];
    if (!selectedSourceIds || selectedSourceIds.length === 0) return [];
    const matched = triggers.filter(
      t =>
        String(t.financialYearId) === String(selectedFinancialYear) &&
        selectedPeriods.some(p => p.fromDate === t.fromDate && p.toDate === t.toDate) &&
        selectedSourceIds.includes(t.sourceId)
    );
    const questionIds = [...new Set(matched.map(t => t.questionId))];
    return questions.filter(q => questionIds.includes(q.questionId));
  }, [triggers, selectedFinancialYear, questions]);

  // ── FY change — resets all filter state ──────────────────────────────────
  const handleFinancialYearChange = (value) => {
    setSelectedFinancialYear(value);
    // Reset Overview
    setOvw_periodType("");
    setOvw_selectedPeriod(null);
    setOvw_sourceId(null);
    setOvw_selectedTagValues([]);
    // Reset Configure
    setCfg_periodType("");
    setCfg_selectedPeriods([]);
    setCfg_sourceIds([]);
    setCfg_questionIds([]);
    setCfg_kpiTagFilter([]);
    setCfg_commonMin("");
    setCfg_commonMax("");
    // Reset Email Configure
    setEcfg_periodType("");
    setEcfg_selectedPeriods([]);
    setEcfg_sourceIds([]);
    setEcfg_questionIds([]);
    setEcfg_kpiTagFilter([]);
    // Reset Email Overview
    setEovw_periodType("");
    setEovw_selectedPeriod(null);
    setEovw_sourceId(null);
    setEovw_selectedTagValues([]);
  };

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // Base data
    financialYears,
    selectedFinancialYear,
    startingMonth,
    sources,
    tags,
    units,
    triggers,
    questions,
    breachedTriggers,
    loadingBreachedTriggers,
    getBreachedTriggers,

    // Overview state
    ovw_periodType, setOvw_periodType,
    ovw_selectedPeriod, setOvw_selectedPeriod,
    ovw_sourceId, setOvw_sourceId,
    ovw_selectedTagValues, setOvw_selectedTagValues,

    // Configure state
    cfg_periodType, setCfg_periodType,
    cfg_selectedPeriods, setCfg_selectedPeriods,
    cfg_sourceIds, setCfg_sourceIds,
    cfg_questionIds, setCfg_questionIds,
    cfg_kpiTagFilter, setCfg_kpiTagFilter,
    cfg_commonMin, setCfg_commonMin,
    cfg_commonMax, setCfg_commonMax,

    // Actions
    handleFinancialYearChange,
    bulkCreateTriggers,
    getTriggers,
    computePeriodSlots,
    resolveBaseUnit,
    resolveBaseUnitId,

    // Overview derived helpers
    getAvailablePeriodTypes,
    getAvailablePeriodSlots,
    getAvailableSources,

    // Email Configure derived helpers
    getEcfg_availablePeriodTypes,
    getEcfg_availablePeriodSlots,
    getEcfg_availableSources,
    getEcfg_availableQuestions,

    // Email Configure state
    ecfg_periodType, setEcfg_periodType,
    ecfg_selectedPeriods, setEcfg_selectedPeriods,
    ecfg_sourceIds, setEcfg_sourceIds,
    ecfg_questionIds, setEcfg_questionIds,
    ecfg_kpiTagFilter, setEcfg_kpiTagFilter,

    // Email Overview state
    eovw_periodType, setEovw_periodType,
    eovw_selectedPeriod, setEovw_selectedPeriod,
    eovw_sourceId, setEovw_sourceId,
    eovw_selectedTagValues, setEovw_selectedTagValues,

    // Email Overview derived helpers
    getEovw_availablePeriodTypes,
    getEovw_availablePeriodSlots,
    getEovw_availableSources,

    // Email recipients action
    bulkUpdateEmailRecipients,

    // Trigger breach schedule
    getBreachSchedule,
    saveBreachSchedule,
  };
};

export default useTriggers;
